import { Button, Col, Form, Input, Modal, Row, Tag } from "antd";
import type React from "react";
import { useEffect } from "react";
import { useUpdateRegistration } from "../hooks/useEventDetail";
import type { EventOccurrence, Registration } from "../types";
import { toJSTDayjs } from "../utils";

interface RegistrationFormValues {
	lastName: string;
	firstName: string;
	lastNameKana: string;
	firstNameKana: string;
	telephone: string;
	email?: string;
	postalCode?: string;
	address?: string;
	building?: string;
	message?: string;
	remarks?: string;
}

interface RegistrationEditModalProps {
	open: boolean;
	onClose: () => void;
	registration?: Registration;
	occurrence?: EventOccurrence;
	primaryColor?: string;
	primaryLightColor?: string;
	onSuccess?: () => void;
}

const SectionHeader: React.FC<{
	title: string;
	bgColor?: string;
	textColor?: string;
}> = ({ title, bgColor, textColor }) => (
	<div
		style={{
			backgroundColor: bgColor,
			padding: "8px",
			textAlign: "center",
			borderRadius: "4px",
			marginBottom: "16px",
			marginTop: "16px",
		}}
	>
		<span style={{ color: textColor, fontWeight: "bold", fontSize: "16px" }}>
			{title}
		</span>
	</div>
);

export const RegistrationEditModal: React.FC<RegistrationEditModalProps> = ({
	open,
	onClose,
	registration,
	occurrence,
	primaryColor = "#99CA29",
	primaryLightColor = "#fefce8",
	onSuccess,
}) => {
	const [form] = Form.useForm();
	const { mutate: updateReg, isPending: isSaving } = useUpdateRegistration();

	useEffect(() => {
		if (open && registration) {
			form.setFieldsValue({
				lastName: registration.lastName,
				firstName: registration.firstName,
				lastNameKana: registration.lastNameKana,
				firstNameKana: registration.firstNameKana,
				telephone: registration.telephone,
				email: registration.email,
				postalCode: registration.postalCode,
				address: registration.address,
				building: registration.building,
				message: registration.message,
				remarks: registration.remarks,
			});
		} else {
			form.resetFields();
		}
	}, [open, registration, form]);

	const handleSubmit = (values: RegistrationFormValues) => {
		if (!registration) return;
		updateReg(
			{ registrationId: registration.registrationId, data: values },
			{
				onSuccess: () => {
					onSuccess?.();
				},
			},
		);
	};

	return (
		<Modal
			title={<span style={{ fontWeight: "bold" }}>予約内容編集</span>}
			open={open}
			onCancel={onClose}
			footer={null}
			destroyOnClose
			centered
			width={600}
			styles={{
				body: { maxHeight: "90vh", overflowY: "auto", padding: "0 24px 24px" },
			}}
		>
			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				<SectionHeader
					title="予約日"
					bgColor={primaryLightColor}
					textColor={primaryColor}
				/>
				<div
					style={{
						textAlign: "center",
						fontSize: "18px",
						fontWeight: "bold",
						marginBottom: "16px",
						color: "#4b5563",
					}}
				>
					{occurrence?.startAt
						? toJSTDayjs(occurrence.startAt).format("YYYY年M月D日")
						: ""}
				</div>

				<SectionHeader
					title="予約時間"
					bgColor={primaryLightColor}
					textColor={primaryColor}
				/>
				<div style={{ textAlign: "center", marginBottom: "16px" }}>
					<Tag
						color={primaryColor}
						style={{
							fontSize: "14px",
							padding: "4px 16px",
							borderRadius: "16px",
							fontWeight: "bold",
						}}
					>
						{occurrence?.startAt
							? toJSTDayjs(occurrence.startAt).format("HH:mm")
							: ""}
						{" 〜 "}
						{occurrence?.endAt
							? toJSTDayjs(occurrence.endAt).format("HH:mm")
							: ""}
					</Tag>
				</div>

				<SectionHeader
					title="お客様情報"
					bgColor={primaryLightColor}
					textColor="#4b5563"
				/>

				<Form.Item
					label="氏名（漢字）"
					required={true}
					style={{ marginBottom: 16 }}
				>
					<Row gutter={8}>
						<Col span={12}>
							<Form.Item name="lastName" noStyle>
								<Input placeholder="性" size="large" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="firstName" noStyle>
								<Input placeholder="名" size="large" />
							</Form.Item>
						</Col>
					</Row>
				</Form.Item>

				<Form.Item
					label="氏名（フリガナ）"
					required={true}
					style={{ marginBottom: 16 }}
				>
					<Row gutter={8}>
						<Col span={12}>
							<Form.Item name="lastNameKana" noStyle>
								<Input placeholder="セイ" size="large" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="firstNameKana" noStyle>
								<Input placeholder="メイ" size="large" />
							</Form.Item>
						</Col>
					</Row>
				</Form.Item>

				<Form.Item name="telephone" label="電話番号" required={true}>
					<Input placeholder="例：08000000012" size="large" allowClear />
				</Form.Item>

				<Form.Item name="email" label="メールアドレス">
					<Input placeholder="例：yamada@pregio.co.jp" size="large" />
				</Form.Item>

				<Form.Item name="postalCode" label="郵便番号">
					<Input placeholder="例：5670000" size="large" />
				</Form.Item>

				<Form.Item label="住所" style={{ marginBottom: 8 }}>
					<Form.Item name="address" noStyle>
						<Input
							placeholder="大阪市中央区南船場..."
							size="large"
							style={{ marginBottom: 8 }}
						/>
					</Form.Item>
					<Form.Item name="building" noStyle>
						<Input placeholder="建物名・部屋番号など" size="large" />
					</Form.Item>
				</Form.Item>

				<SectionHeader
					title="メッセージ"
					bgColor={primaryLightColor}
					textColor={primaryColor}
				/>
				<Form.Item name="message">
					<Input.TextArea rows={3} placeholder="メッセージを入力してください" />
				</Form.Item>

				<SectionHeader
					title="管理者だけへの備考"
					bgColor={primaryLightColor}
					textColor={primaryColor}
				/>
				<Form.Item name="remarks">
					<Input.TextArea
						rows={3}
						placeholder="この予約に対して追記事項がございましたら、ご入力ください。"
					/>
				</Form.Item>

				<div
					style={{ display: "flex", justifyContent: "center", marginTop: 32 }}
				>
					<Button
						type="primary"
						htmlType="submit"
						size="large"
						loading={isSaving}
						style={{
							backgroundColor: primaryColor,
							borderColor: primaryColor,
							height: "48px",
							padding: "0 80px",
							fontSize: "18px",
							fontWeight: "bold",
							borderRadius: "4px",
						}}
					>
						保存する
					</Button>
				</div>
			</Form>
		</Modal>
	);
};
